import styled from 'styled-components';
import { useQuery } from 'react-query';
import { list_intermediary_underwriters } from 'services/apiSrv';

const CARD_HEIGHT = '448px'; // total card height — adjust after visual test
const IMAGE_HEIGHT = '176px'; // fixed — same for ALL cards
function truncateString(str='') {
  if (str.length > 40) {
    // Truncate the string to the first 40 characters
    str = str.slice(0, 40); 
    // Add ellipses to the end
    str = str + '...'; 
  }
  return str;
}


const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${CARD_HEIGHT};
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: all 0.25s ease;
  position: relative;
  outline: 2px solid transparent;
  outline-offset: -2px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 28px -6px rgba(0,0,0,0.12), 0 8px 12px -6px rgba(0,0,0,0.06);
    outline: 2px solid #c5c3ff;
  }

  &:focus-within {
    outline: 2px solid #a29bfe;
  }
`;

const ImageSection = styled.div`
  width: 100%;
  height: ${IMAGE_HEIGHT};
  background: #f8f9fc;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0; /* prevents compression */
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: transform 0.4s ease;
  
  ${Card}:hover & {
    transform: scale(1.04);
  }
`;

const ContentWrapper = styled.div`
  padding: 20px 20px 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 16px;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.4;
`;

const Underwriter = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin: 2px 0 0 0;
  letter-spacing: 0.4px;
`;

const Description = styled.p`
  font-size: 12px;
  color: #4b5563;
  margin: 4px 0 0 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;           /* ← strictly 2 lines max */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
`;

const PriceLabel = styled.span`
  color: #6b7280;
  font-weight: 500;
  min-width: 96px;
`;

const PriceValue = styled.span`
  font-weight: 700;
  color: #111827;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  font-size: 12px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 8px;

  @media (max-width: 360px) {
    flex-direction: column;
    gap: 10px;

    & > button {
      width: 100%;
    }
  }
`;

const ActionButton = styled.button`
  flex: 1 1 auto;
  min-width: 82px;
  background: #7151e6;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #6246d4;
    transform: translateY(-1.5px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(113, 81, 230, 0.4);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingMessage = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0 0 8px 0;
  font-style: italic;
`;

const ErrorMessage = styled.p`
  font-size: 13px;
  color: #ef4444;
  margin: 0 0 12px 0;
  font-weight: 500;
`;

const ProductCard = ({ product, onView, onEdit, onDelete }) => {
  const {
    data: underwritersData,
    isLoading: isLoadingUnderwriter,
    isError: isUnderwritersError,
    error: underwriterError,
  } = useQuery({
    queryKey: ['underwriters'],
    queryFn: list_intermediary_underwriters,
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const underwriters = underwritersData?.data?._value ?? [];
  const matchedUnderwriter = underwriters.find(
    (uw) => uw.underwriterSystemRef === product.underwriterSystemRef
  );

  const handleView = () => onView?.(product);
  const handleEdit = () => onEdit?.(product);
  const handleDelete = () => onDelete?.(product);

  const fallbackImage = 'https://images.unsplash.com/photo-1724482606633-fa74fe4f5de1?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

  return (
    <Card role="article" aria-label={`Insurance product: ${product.productName || 'Unnamed'}`}>
      <ImageSection>
        <ProductImage
          src={product?.productLogo || fallbackImage}
          alt={`${product.productName || 'Product'} logo`}
          loading="lazy"
          onError={(e) => {
            e.target.src = fallbackImage;
          }}
        />
      </ImageSection>

      <ContentWrapper>
        {isLoadingUnderwriter && <LoadingMessage>Loading underwriter info...</LoadingMessage>}
        {isUnderwritersError && (
          <ErrorMessage>
            {underwriterError?.message || 'Could not load underwriter'}
          </ErrorMessage>
        )}

        <div>
          <Title>{product.productName || 'Unnamed Product'}</Title>
          <Underwriter>
            {matchedUnderwriter
              ? `BY ${matchedUnderwriter.name?.toUpperCase() || 'UNKNOWN'}`
              : 'BY INTERMEDIARY'}
          </Underwriter>
          <Description>
          - {truncateString(product.productDescription) || 'No description provided for this product.'}
          </Description>
        </div>

        <div>
          <PriceContainer>
            <PriceRow>
              <PriceLabel>Min Premium:</PriceLabel>
              <PriceValue>
                {product.supportedCurrency || 'KES'} {product.productPremium?.minimum ?? '—'}
              </PriceValue>
            </PriceRow>
            <PriceRow>
              <PriceLabel>Max Premium:</PriceLabel>
              <PriceValue>
                {product.supportedCurrency || 'KES'} {product.productPremium?.maximum ?? '—'}
              </PriceValue>
            </PriceRow>
          </PriceContainer>

          <ButtonContainer>
            <ActionButton type="button" onClick={handleView}>
              VIEW
            </ActionButton>
            <ActionButton type="button" onClick={handleEdit}>
              EDIT
            </ActionButton>
            <ActionButton type="button" onClick={handleDelete}>
              DELETE
            </ActionButton>
          </ButtonContainer>
        </div>
      </ContentWrapper>
    </Card>
  );
};

export default ProductCard;